import { useEffect, useState } from "react";
import AdminLayout from "./components/AdminLayout";
import { supabase } from "@/lib/supabase";
import type { Testimonial } from "@/lib/supabase";

export default function DepoimentosAdminPage() {
  const [items, setItems] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Partial<Testimonial> | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    const { data } = await supabase.from("testimonials").select("*").order("display_order");
    setItems(data ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openNew = () => { setEditing({ author: "", company: "", role: "", text: "", rating: 5, active: true }); setShowModal(true); };
  const openEdit = (t: Testimonial) => { setEditing(t); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditing(null); };

  const handleSave = async () => {
    if (!editing?.author || !editing?.text) return;
    setSaving(true);
    if (editing.id) {
      await supabase.from("testimonials").update({ author: editing.author, company: editing.company, role: editing.role, text: editing.text, rating: editing.rating, active: editing.active }).eq("id", editing.id);
    } else {
      await supabase.from("testimonials").insert({ author: editing.author, company: editing.company, role: editing.role, text: editing.text, rating: editing.rating ?? 5, active: editing.active ?? true });
    }
    setSaving(false);
    closeModal();
    fetchData();
  };

  const handleDelete = async (id: string) => {
    await supabase.from("testimonials").delete().eq("id", id);
    setDeleteId(null);
    fetchData();
  };

  const StarIcon = ({ filled }: { filled: boolean }) => (
    <i className={filled ? "ri-star-fill" : "ri-star-line"}></i>
  );

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Depoimentos</h1>
          <p className="text-gray-500 text-sm mt-1">{items.length} depoimento(s)</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 bg-[#1B1F5E] hover:bg-[#2F4F8F] text-white font-bold text-sm px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></div>
          Novo Depoimento
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-3 flex items-center justify-center h-40"><div className="w-6 h-6 flex items-center justify-center text-[#1B1F5E]"><i className="ri-loader-4-line animate-spin text-2xl"></i></div></div>
        ) : items.length === 0 ? (
          <div className="col-span-3 text-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-chat-quote-line text-4xl"></i></div>
            <p className="text-sm">Nenhum depoimento cadastrado</p>
          </div>
        ) : items.map(t => (
          <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-0.5 text-yellow-400">
                {[1,2,3,4,5].map(s => (
                  <div key={s} className="w-3 h-3 flex items-center justify-center text-xs">
                    <StarIcon filled={s <= (t.rating ?? 5)} />
                  </div>
                ))}
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>{t.active ? "Ativo" : "Inativo"}</span>
            </div>
            <p className="text-sm text-gray-600 mb-4 line-clamp-3">&ldquo;{t.text}&rdquo;</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-[#1a1a1a]">{t.author}</p>
                <p className="text-xs text-gray-500">{t.role}{t.company ? ` — ${t.company}` : ""}</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(t)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-[#1B1F5E] transition-colors cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                <button onClick={() => setDeleteId(t.id)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-[#1a1a1a]">{editing.id ? "Editar Depoimento" : "Novo Depoimento"}</h2>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl"></i></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-1.5">Nome *</label>
                  <input value={editing.author ?? ""} onChange={e => setEditing({ ...editing, author: e.target.value })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="Nome do cliente" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-1.5">Empresa</label>
                  <input value={editing.company ?? ""} onChange={e => setEditing({ ...editing, company: e.target.value })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="Empresa" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">Cargo</label>
                <input value={editing.role ?? ""} onChange={e => setEditing({ ...editing, role: e.target.value })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="Cargo" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">Depoimento *</label>
                <textarea value={editing.text ?? ""} onChange={e => setEditing({ ...editing, text: e.target.value })} rows={4} maxLength={500} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E] resize-none" placeholder="Texto do depoimento..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333] mb-2">Avaliação</label>
                <div className="flex items-center gap-2">
                  {[1,2,3,4,5].map(s => (
                    <button key={s} type="button" onClick={() => setEditing({ ...editing, rating: s })} className="w-8 h-8 flex items-center justify-center cursor-pointer text-yellow-400 text-xl">
                      <StarIcon filled={s <= (editing.rating ?? 5)} />
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editing.active ?? true} onChange={e => setEditing({ ...editing, active: e.target.checked })} className="w-4 h-4 accent-[#1B1F5E]" />
                <span className="text-sm text-[#333]">Ativo no site</span>
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={closeModal} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-[#1B1F5E] hover:bg-[#2F4F8F] text-white font-bold text-sm rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-60">{saving ? "Salvando..." : "Salvar"}</button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4"><i className="ri-delete-bin-line text-red-500 text-xl"></i></div>
            <h3 className="text-base font-bold text-[#1a1a1a] mb-2">Excluir depoimento?</h3>
            <p className="text-gray-500 text-sm mb-6">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold text-sm rounded-lg cursor-pointer whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
